import { Dialog, colorValue } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { isNil } from 'ramda';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Status } from 'src/js/component/actionBar/actionBar';
import { NoContentAvailable } from 'src/js/component/no-content-available';
import { SortType } from 'src/js/component/sort-manager';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { useStores } from 'src/js/hook/use-stores';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { fetchEssences } from 'src/js/pages/streaming/essence/essence-store';
import { StreamingActionBar, scrollPanelIntoView } from 'src/js/component/actionBar/streaming-action-bar';
import { fetchVideoEncoders } from 'src/js/pages/streaming/video-encoder/video-encoder-store';
import { fetchVideoInputs } from 'src/js/pages/streaming/video-input/video-input-store';
import { useWatchObject } from 'src/js/store/use-watch';
import { PRODUCT, constant } from 'src/js/util/constant';
import { isNilOrEmpty } from 'src/js/util/global-util';

import { AddMetadata } from './add-metadata-modal';
import { MetadataList } from './metadata-list';
import {
  MetadataViewModel,
  metadataIsStarted,
  metadataIsStopped,
  metadataModelToStatus,
  newMetadataViewModel,
} from './metadata-models';
import { MetadataAddFormRef } from './metadata-edit';
import metadataModels, { deleteMetadata, fetchMetadata, startStopMetadata } from './metadata-store';
import { useLocalStorage } from 'usehooks-ts';

export const MetadataCtx = createContext(undefined);

let connectionLost = false;
export const MetadataPage: React.FunctionComponent = (props) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const [addModalShown, showAddModal, hideAddModal] = useModalControls();
  const addMetadataRef = useRef<MetadataAddFormRef>();
  const [addButtonState, setAddButtonState] = useState<ButtonStateType>(undefined);
  const newModel = newMetadataViewModel(t);
  const [metadata, setMetadata] = useState<MetadataViewModel[]>(metadataModels);
  const [initialized, setInitialized] = useState(false);
  useWatchObject(metadataModels, setMetadata);

  useAdaptiveFetch(
    () =>
      fetchMetadata(t).then((res) => {
        if (res.ok) {
          setInitialized(true);
          if (connectionLost) {
            triggerEvent('connection-restored');
            connectionLost = false;
          }
        } else if (res.problem === 'NETWORK_ERROR') {
          triggerEvent('connection-lost');
          connectionLost = true;
        }
      }),
    5000,
  );

  const fetchData = async () => {
    systemInfoCapabilities.is2110() ? await fetchEssences(t) : await fetchVideoInputs();
    fetchVideoEncoders(t);
  };

  useEffect(() => {
    fetchData();
    triggerEvent('metadata-fetched');
  }, []);

  watchEvent('preset-load', () => {
    fetchMetadata(t);
    fetchData();
  });

  const filteredStart = () => metadata.filter((e) => e.selected && metadataIsStopped(e.state));
  const handleStartMetadata = () => {
    const filtered = filteredStart();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopMetadata(e, true, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('metadata.notifications.startErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredStop = () => metadata.filter((e) => e.selected && metadataIsStarted(e.state));
  const handleStopMetadata = () => {
    const filtered = filteredStop();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopMetadata(e, false, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('metadata.notifications.stopErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const [deleteShown, showDelete, hideDelete] = useModalControls();
  const filteredDelete = () => metadata.filter((e) => e.selected && e.id > systemInfo.videoInputs);
  const handleDeleteMetadata = () => {
    const filtered = filteredDelete();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => deleteMetadata(e))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 0) {
          createAndDispatchNotification(
            t('metadata.notifications.deleteErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const statusTypes: Status[] = [
    { status: 'ACTIVE', color: colorValue('haiui-green-01') },
    { status: 'ALERT', color: colorValue('haiui-amber-01') },
    { status: 'INACTIVE', color: colorValue('haiui-gray-07') },
  ];

  const handleSelect = (item: MetadataViewModel, selected: boolean) => {
    const metadataModels = metadata.map((i: MetadataViewModel) => (i.id === item.id ? { ...i, selected } : i));
    setMetadata(metadataModels);
  };

  const onSelectAll = (selected: boolean) => {
    const models = metadata.map((i) => ({ ...i, selected }));
    setMetadata(models);
  };

  const handleOnKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter') {
      const button = document.getElementById('metadata-add-form-submit');
      if (!isNilOrEmpty(button)) {
        e.preventDefault();
        button.click();
      }
    }
  };

  const handleAddMetadata = () => {
    setAddButtonState('pending');
    addMetadataRef.current.submitAddMetadata().then((res: any) => {
      setAddButtonState(undefined);
      if (res?.ok) {
        const response = res.data;
        hideAddModal();
        scrollPanelIntoView({ id: response.info.id }, true);
      }
    });
  };

  const [searchFilters, setSearchFilters] = useLocalStorage(
    `${constant.lStorage.searchFilter}:${sessionStore.username}:metadata`,
    [],
  );

  const sortInfo = {
    id: { title: t('sort.sortText.id'), type: SortType.GENERIC },
    name: { title: t('sort.sortText.name'), type: SortType.NATURAL_SORT },
    typeTextual: { title: t('sort.sortText.type'), type: SortType.NATURAL_SORT },
    formatTxt: { title: t('sort.sortText.format'), type: SortType.NATURAL_SORT },
    stateSortable: { title: t('sort.sortText.status'), type: SortType.GENERIC },
  };

  return (
    <MetadataCtx.Provider value={{ metadata, setMetadata }}>
      {initialized && (isNil(metadata) || metadata.length === 0) ? (
        <NoContentAvailable
          title={t('metadata.noContent.title')}
          subText={t('metadata.noContent.subText')}
          btnText={t('metadata.noContent.btnText')}
          iconName="Streams"
          onClick={showAddModal}
          btnDisabled={sessionStore.isUser()}
        />
      ) : (
        <StreamingActionBar
          items={metadata}
          searchKeys={['name', 'typeTextual', 'formatTxt', 'paramsTxt']}
          initialSearchFilters={searchFilters}
          onChangeSearchFilters={setSearchFilters}
          statusTypes={statusTypes}
          sortInfo={sortInfo}
          getStateValue={metadataModelToStatus}
          scrollToId={(props as any).scrollToId}
          actionButtons={
            sessionStore.isUser()
              ? []
              : [
                  {
                    onClick: handleStartMetadata,
                    counter: filteredStart,
                    label: t('general.start'),
                  },
                  { onClick: handleStopMetadata, counter: filteredStop, label: t('general.stop') },
                  { onClick: showDelete, counter: filteredDelete, label: t('general.deleteBtn') },
                ]
          }
          List={MetadataList}
          onSelectAll={onSelectAll}
          updateSelection={handleSelect}
          storageKey="metadata"
          actionRightPrimary={
            sessionStore.isUser()
              ? null
              : {
                  onClick: showAddModal,
                  disabled: metadata?.findIndex((s) => s.id === -1) >= 0,
                  children: t('metadata.settings.addMetadata'),
                }
          }
          placeholderRightComponents={1}
          placeholderNumPanels={constant.product === PRODUCT.FX ? 1 : 4}
        />
      )}
      {deleteShown && (
        <Dialog
          headline={t('metadata.settings.modal.delete.title', { count: filteredDelete().length })}
          bodyText={`${t('metadata.settings.modal.delete.bodyText')} ${t('general.cannotUndo')}`}
          onClose={hideDelete}
          show={true}
          buttons={[
            { label: t('general.cancel') },
            { label: t('general.deleteBtn'), variant: 'danger', onClick: handleDeleteMetadata },
          ]}
        />
      )}
      {addModalShown && (
        <Dialog
          title={t('metadata.settings.addMetadata')}
          dialogType="activity"
          headerIcon="Metadata"
          accentColor={constant.productColor}
          size="lg"
          className="mk-three-columns"
          content={<AddMetadata model={newModel} ref={addMetadataRef} />}
          onClose={hideAddModal}
          onKeyUp={handleOnKeyUp}
          buttons={[
            { variant: 'secondary', onClick: hideAddModal, label: t('general.cancel') },
            {
              id: 'metadata-add-form-submit',
              variant: 'primary',
              state: addButtonState,
              close: false,
              onClick: handleAddMetadata,
              label: t('metadata.settings.addMetadata'),
            },
          ]}
        />
      )}
    </MetadataCtx.Provider>
  );
};
